import React from 'react';
import { Servicess } from '../data/data';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';

const Services = () => {
  const { t } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { type: "spring", stiffness: 100, damping: 20 } 
    }
  };

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative">
      {/* Dynamic Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-blue-500/5 dark:bg-blue-600/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-purple-500/5 dark:bg-purple-600/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header Text */}
        <div className="text-center mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('servicesLabel', 'Expertise')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('our_services')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
          <p className="mt-4 text-slate-500 dark:text-neutral-400 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            {t('service_tagline')}
          </p>
        </div>

        {/* Services Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {Servicess.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="group relative p-6 sm:p-8 rounded-3xl bg-slate-50/50 hover:bg-white dark:bg-[#111111]/30 dark:hover:bg-[#111111] border border-slate-200/50 dark:border-neutral-800/40 hover:border-blue-500/30 dark:hover:border-purple-500/30 hover:shadow-xl hover:shadow-blue-500/5 dark:hover:shadow-purple-500/5 transition-all duration-300 overflow-hidden flex flex-col justify-between min-h-[250px]"
            >
              {/* Card Hover Gradient Overlay */}
              <div className="absolute -inset-px bg-gradient-to-br from-blue-500/5 to-purple-500/5 dark:from-purple-500/5 dark:to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl" />
              
              <div>
                {/* Icon wrapper */}
                <div className="relative z-10 w-12 h-12 flex items-center justify-center rounded-2xl bg-blue-50 dark:bg-neutral-900 text-blue-600 dark:text-purple-400 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 dark:group-hover:from-purple-500 dark:group-hover:to-pink-500 group-hover:text-white dark:group-hover:text-white transition-all duration-300 mb-6">
                  <service.icon className="w-6 h-6" />
                </div>

                {/* Title */}
                <h3 className="relative z-10 text-lg font-extrabold text-slate-900 dark:text-white mb-3">
                  {t(service.title, service.title.replace(/_/g, ' '))}
                </h3>

                {/* Subtitle */}
                <p className="relative z-10 text-sm text-slate-500 dark:text-neutral-400 leading-relaxed mb-6">
                  {t(service.subtitle, 'Professional solutions tailored for your business needs.')}
                </p>
              </div>

              {/* Learn More link */}
              <div className="relative z-10 flex items-center gap-2 text-xs font-bold text-blue-600 dark:text-purple-400 hover:text-blue-700 dark:hover:text-purple-300 cursor-pointer w-fit mt-auto group/btn">
                <span>{t('learn_more', 'Learn More')}</span>
                <FiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;